import { LyMainProductFormBase } from "./base"

if (!customElements.get('ly-main-product-form-order-summary')) {
    customElements.define('ly-main-product-form-order-summary', class extends LyMainProductFormBase {

        #PRE_ORDER_TAG = 'pre-order'
        #LOW_STOCK_TAG = 'low-stock'
        #IN_STOCK_TAG = 'in-stock'
        #NO_STOCK_TAG = 'no-stock'

        get shipments() {
            return [...this.shipmentWrapper.querySelectorAll('[data-shipment]')]
        }

        async connectedCallback() {
            super.connectedCallback()

            await customElements.whenDefined('ly-popover-v5')

            this.detailPopover = this.querySelector('ly-popover-v5.detail-popover')
            this.shipmentWrapper = this.detailPopover.bodyContent.querySelector('[data-shipments]')

            const script = this.querySelector('[data-block-settings]')
            this.blockSettings = readJsonFromScript(script)

            this.sidewallChangeUnsubscriber = window.subscribe('bus:sidewall_picker_changed', this.handleSidewallChange)
            this.sidewallAccessoryChangeUnsubscriber = window.subscribe('bus:sidewall_accessory_picker_changed', this.handleSidewallAccessoryChange)
            this.accessoryChangeUnsubscriber = window.subscribe('bus:accessory_picker_changed', this.handleAccessoryChange)
        }

        disconnectedCallback() {
            this.sidewallChangeUnsubscriber()
            this.sidewallAccessoryChangeUnsubscriber()

            super.disconnectedCallback()
        }

        _onVariantChange = ({ variantId }) => {
            this.refresh(this._variantConfigs[variantId])
        }

        refresh = (newConfig) => {
            this.shipments.forEach(shipment => {
                if (this.isDefaultShipment(shipment)) return
                shipment.remove()
            })
            this.updateShippingCount()
            const defaultShipment = this.shipments.find(this.isDefaultShipment)

            this.setShipmentStock(defaultShipment, newConfig.stock_class)

            const stockTitle = defaultShipment.querySelector('[data-stock-title]')
            stockTitle.innerHTML = newConfig.stock_title

            const shipTime = defaultShipment.querySelector('[data-ship-time]')
            shipTime.querySelector('ly-format-time').dataset.time = newConfig.pre_order_time

            const variantDoms = this.getVariantDomsFromShipment(defaultShipment)
            variantDoms.forEach(variantDom => {
                if (this.isCurrentVariantDom(variantDom)) return
                variantDom.remove()
            })
            const variantDom = variantDoms.find(this.isCurrentVariantDom)

            variantDom.dataset.variant = newConfig.sku_id

            const variantImage = variantDom.querySelector('[data-variant-image]')
            variantImage.innerHTML = this.createVariantImageHtml(newConfig.sku_image)

            const variantOptions = variantDom.querySelector('[data-variant-options]')
            variantOptions.innerHTML = newConfig.sku_options
        }

        isDefaultShipment = (shipment) => {
            return shipment.classList.contains('default')
        }

        isCurrentVariantDom = (variantDom) => {
            return variantDom.classList.contains('current')
        }

        setShipmentStock = (shipment, stock) => {
            shipment.dataset.stock = stock

            const stockTitle = shipment.querySelector('[data-stock-title]')
            stockTitle.dataset.stock = stock

            const shipTime = shipment.querySelector('[data-ship-time]')
            shipTime.dataset.stock = stock
        }

        getVariantDomsFromShipment = (shipment) => {
            return [...shipment.querySelectorAll('[data-variant]')]
        }

        createVariantImageHtml = (url) => {
            const ws = [60, 90, 120, 180]
            const src = `${url}&width=${ws[0]}`
            const srcset = ws.map(w => {
                return `${url}&width=${w} ${w}w`
            }).join(', ')

            return `
            <img src="${src}"
            srcset="${srcset}"
            sizes="(max-width: 767px) 61px, 93px"
            width="61"
            height="46"
            loading="lazy">
            `
        }

        handleSidewallChange = ({ variantId, checked }) => {
            const target = this.sidewallCheckedInputs.find(i => i.dataset.variantId === variantId)
            if (target) {
                this.updateChange(variantId, checked, target)
            }
            this.fixAllStatue()
        }

        handleSidewallAccessoryChange = ({ variantId, checked }) => {
            const target = this.sidewallAccessoryCheckedInputs.find(i => i.dataset.variantId === variantId)
            if (target) {
                this.updateChange(variantId, checked, target)
            }
            this.fixAllStatue()
        }

        handleAccessoryChange = ({ id: variantId, checked }) => {
            const target = this.accessoryCheckedResult
                .find(item => item.checkedInput.dataset.variantId === variantId)?.checkedInput
            if (target) {
                this.updateChange(variantId, checked, target)
            }
            this.fixAllStatue()
        }

        updateChange = (variantId, checked, target) => {
            const config = this.getConfig(target)
            if (!config) return

            let shipment = this.getShipment(config)
            if (!shipment) {
                shipment = this.createShipment(config)
            }
            const variantDoms = this.getVariantDomsFromShipment(shipment)
            const variantDom = variantDoms.find(variantDom => variantDom.dataset.variant === variantId)

            if (!variantDom && checked) {
                const newVariantDom = this.createVariantDom(config)
                const wrapper = shipment.querySelector('[data-variants]')
                wrapper.appendChild(newVariantDom)
                return
            }

            const { countDom, currentCount } = this.getVariantCount(variantDom)

            if (checked) {
                countDom.innerHTML = currentCount + 1
            } else {
                if (variantDom) {
                    const newCount = currentCount - 1
                    if (newCount <= 0) {
                        variantDom.remove()
                    } else {
                        countDom.innerHTML = newCount
                    }
                }
            }
        }

        getConfig(target) {
            const script = target.parentElement.querySelector('script[type="application/json"][data-order-summary-sku-config]')

            if (!script) return null
            const config = readJsonFromScript(script)

            return config
        }

        getShipment = (config) => {
            const { stock_class } = config
            if (stock_class === this.#PRE_ORDER_TAG) {
                return this.shipments
                    .filter(item => item.dataset.stock === stock_class)
                    .find(item => {
                        const pre_order_time = item.querySelector('ly-format-time').dataset.time
                        return pre_order_time === config.pre_order_time
                    })
            }

            if (stock_class === this.#NO_STOCK_TAG) {
                return this.shipments.find(item => item.dataset.stock === stock_class)
            }

            return this.shipments.find(item => [this.#LOW_STOCK_TAG, this.#IN_STOCK_TAG].includes(item.dataset.stock))
        }

        createShipment = (config) => {
            const shipment = document.createElement('div')
            shipment.className = 'ly-main-product-form-order-summary-detail-popover-shipment'
            shipment.setAttribute('data-shipment', '')
            shipment.setAttribute('data-stock', config.stock_class)

            shipment.innerHTML = `
            <div class="ly-main-product-form-order-summary-detail-popover-shipment-header">
              <div class="ly-main-product-form-order-summary-detail-popover-shipment-title">
                <span class="icon-wrapper">
                  ${this.createShipmentIconHtml(this.shipments.length)}
                </span>
                <span class="text-wrapper">
                  ${this.blockSettings.detail_popover_shipment_title}
                </span>
                <span class="no-wrapper" data-sort>
                  ${this.shipments.length + 1}
                </span>
              </div>
              <div class="ly-main-product-form-order-summary-detail-popover-shipment-stock-status" data-stock-title data-stock="${config.stock_class}">
                ${config.stock_title}
              </div>
            </div>

            <div class="ly-main-product-form-order-summary-detail-popover-shipment-ship-time" data-ship-time data-stock="${config.stock_class}">
              <span class="time-title normal">${this.blockSettings.detail_popover_ship_time_title}</span>
              <span class="time-title pre-order">${this.blockSettings.detail_popover_ship_time_title_pre_order}</span>
              <span class="time-title no-stock">${this.blockSettings.detail_popover_ship_time_title_no_stock}</span>
              <span class="ship-time normal">${this.blockSettings.detail_popover_ship_time}</span>
              <span class="ship-time pre-order">
                <span>${this.blockSettings.detail_popover_ship_time_pre_order}</span>
                <ly-format-time data-time="${config.pre_order_time}"></ly-format-time>
              </span>
              <span class="ship-time no-stock">${this.blockSettings.detail_popover_ship_time_no_stock}</span>
            </div>

            <div class="ly-main-product-form-order-summary-detail-popover-shipment-variants" data-variants>
            </div>
            `

            this.shipmentWrapper.appendChild(shipment)

            return shipment
        }

        createShipmentIconHtml = (index) => {
            const url = this.blockSettings.shipment_icons[index]
            const ws = [30, 60, 90]
            const src = `${url}&width=${ws[0]}`
            const srcset = ws.map((w, i) => {
                return `${url}&width=${w} ${i + 1}x`
            }).join(',')

            return `
            <img
              src="${src}"
              srcset="${srcset}"
              width="23"
              height="22"
              loading="lazy"
            >
            `
        }

        createVariantDom = (config) => {
            const variantDom = document.createElement('div')
            variantDom.className = 'ly-main-product-form-order-summary-detail-popover-shipment-variant'
            variantDom.dataset.variant = config.sku_id
            variantDom.dataset.stock = config.stock_class

            variantDom.innerHTML = `
            <div class="ly-main-product-form-order-summary-detail-popover-shipment-variant-image" data-variant-image>
              ${this.createVariantImageHtml(config.sku_image)}
            </div>

            <div class="ly-main-product-form-order-summary-detail-popover-shipment-variant-info">
              <div class="ly-main-product-form-order-summary-detail-popover-shipment-variant-title">
                ${config.sku_title}
              </div>
              <div class="ly-main-product-form-order-summary-detail-popover-shipment-variant-options-wrapper">
                <div class="ly-main-product-form-order-summary-detail-popover-shipment-variant-options" data-variant-options>
                  ${config.sku_options}
                </div>
                <div class="ly-main-product-form-order-summary-detail-popover-shipment-variant-count">
                  <span>x</span>
                  <span data-variant-count>1</span>
                </div>
              </div>
            </div>    
            `

            return variantDom
        }

        getVariantCount = (variantDom) => {
            const countDom = variantDom.querySelector('[data-variant-count]')
            const currentCount = Number(countDom.innerHTML.trim())
            return { countDom, currentCount }
        }

        fixAllStatue = () => {
            const _updateCount = (variantDom, count) => {
                if (count > 0) {
                    const { countDom } = this.getVariantCount(variantDom)
                    countDom.innerHTML = count
                }

                return count > 0
            }
            this.shipments.forEach(shipment => {
                let variantDoms = this.getVariantDomsFromShipment(shipment)
                variantDoms.forEach(variantDom => {
                    if (this.isCurrentVariantDom(variantDom)) return
                    const variantId = variantDom.dataset.variant
                    let shouldExists = false
                    if (!shouldExists) {
                        const checkedInputs = this.sidewallCheckedInputs.filter(i => i.dataset.variantId === variantId)
                        shouldExists = _updateCount(variantDom, checkedInputs.length)
                    }

                    if (!shouldExists) {
                        const checkedInputs = this.sidewallAccessoryCheckedInputs.filter(i => i.dataset.variantId === variantId)
                        shouldExists = _updateCount(variantDom, checkedInputs.length)
                    }

                    if (!shouldExists) {
                        const checkedResult = this.accessoryCheckedResult.find(item => item.checkedInput.dataset.variantId === variantId)
                        let count = null
                        if (!checkedResult) {
                            count = 0
                        } else {
                            count = Number(checkedResult.quantityInput.value)
                        }
                        shouldExists = _updateCount(variantDom, count)
                    }

                    if (!shouldExists) {
                        variantDom.remove()
                    }
                })
                variantDoms = this.getVariantDomsFromShipment(shipment)
                if (variantDoms.length <= 0) {
                    shipment.remove()
                    return
                }
                if (![this.#PRE_ORDER_TAG, this.#NO_STOCK_TAG].includes(shipment.dataset.stock)) {
                    const lowStockIndex = variantDoms.findIndex(variantDom => {
                        return variantDom.dataset.stock === this.#LOW_STOCK_TAG
                    })

                    const stock = lowStockIndex !== -1 ? this.#LOW_STOCK_TAG : this.#IN_STOCK_TAG
                    this.setShipmentStock(shipment, stock)
                }
            })

            this.shipments.forEach((shipment, index) => {
                const sort = shipment.querySelector('[data-sort]')
                const oldI = Number(sort.innerHTML.trim())
                const newI = index + 1
                if (oldI === newI) return
                sort.innerHTML = newI

                const iconWrapper = shipment.querySelector('.icon-wrapper')
                iconWrapper.innerHTML = this.createShipmentIconHtml(index)
            })

            this.updateShippingCount()
        }

        updateShippingCount = () => {
            const shippingCountDom = this.detailPopover.bodyContent.querySelector('[data-shipping-count]')
            if (shippingCountDom) {
                shippingCountDom.innerHTML = this.shipments.length
            }
        }
    })
}